import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { ApiError, violationMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatInrMinor } from '../../lib/constants';
import { ExamFeeNotice } from '../components/ExamFeeNotice';
import { ParentShell } from '../components/ParentShell';
import { PaymentTable } from '../components/PaymentRow';
import { fetchMyPayments, learnerNavFromSummary } from '../lib/parent-portal-api';
import { fetchMyLearners } from '../lib/registration-api';

export function PaymentsPage(): ReactElement {
  const { token, loading } = useAuth();
  const [learnerFilter, setLearnerFilter] = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');

  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
    enabled: Boolean(token),
  });

  const paymentsQuery = useQuery({
    queryKey: ['my-payments', learnerFilter, purposeFilter],
    queryFn: () => fetchMyPayments({
      learnerId: learnerFilter || undefined,
      purpose: purposeFilter || undefined,
    }),
    enabled: Boolean(token),
  });

  const learners = learnerNavFromSummary(learnersQuery.data ?? []);
  const summary = paymentsQuery.data?.summary;

  const filteredItems = useMemo(() => paymentsQuery.data?.items ?? [], [paymentsQuery.data]);

  if (!loading && !token) return <Navigate to="/login" replace />;

  return (
    <ParentShell learners={learners} title="Payments">
      {paymentsQuery.error ? (
        <div className="error-banner" role="alert">
          {paymentsQuery.error instanceof ApiError
            ? violationMessage(paymentsQuery.error.problem)
            : 'Could not load payments'}
        </div>
      ) : null}

      {summary ? (
        <div className="summary-tiles">
          <article className="tile">
            <p className="tile-label">Paid to date</p>
            <p className="tile-value money">{formatInrMinor(summary.paidToDateMinor)}</p>
          </article>
          <article className="tile">
            <p className="tile-label">Outstanding</p>
            <p className="tile-value money">{formatInrMinor(summary.outstandingMinor)}</p>
          </article>
          <article className="tile">
            <p className="tile-label">Transactions</p>
            <p className="tile-value">{summary.transactionCount}</p>
          </article>
        </div>
      ) : null}

      <div className="field-row filter-bar">
        <label className="field">
          <span className="field-label">Child</span>
          <select value={learnerFilter} onChange={(e) => setLearnerFilter(e.target.value)}>
            <option value="">All children</option>
            {learners.map((learner) => (
              <option key={learner.learnerId} value={learner.learnerId}>
                {learner.firstName} {learner.lastName}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Purpose</span>
          <select value={purposeFilter} onChange={(e) => setPurposeFilter(e.target.value)}>
            <option value="">All purposes</option>
            <option value="REGISTRATION">Registration fee</option>
            <option value="SUBJECT_FEES">Subject fees</option>
          </select>
        </label>
      </div>

      {paymentsQuery.isLoading ? <p className="text-muted">Loading…</p> : <PaymentTable items={filteredItems} />}

      <div className="section-gap">
        <ExamFeeNotice />
      </div>
    </ParentShell>
  );
}
